/**
 * Contact API service for fetching visitors and customers.
 */
import { HttpClient } from '@/api/services/http';
import type { Contact } from '@/types/contact';
import { CustomersResponse } from './customerService';
import { MAX_RETRIES } from './http/config';

const ALL_CONTACTS_URL = '/customer';
// const VISITORS_URL = '/customer/visitors';
// const CUSTOMERS_URL = '/customer/customers';

export const contactService = {
  async fetchAllContacts(retryCount = 0): Promise<CustomersResponse> {
    try {
      console.log(`Attempting to fetch all contacts (try ${retryCount + 1}/${MAX_RETRIES + 1})`);
      const workspaceId = localStorage.getItem('workspaceId');
      console.log('Fetching all contacts with workspace ID:', workspaceId);

      const response = await HttpClient.apiClient.get<CustomersResponse>(ALL_CONTACTS_URL, {
        params: { workspace_id: workspaceId }
      });
      console.log(`Successfully fetched all contacts:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching all contacts:', error);
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying fetchAllContacts (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, retryCount)));
        return this.fetchAllContacts(retryCount + 1);
      }
      throw error.isServerError
        ? new Error('Server error. Please try again later.')
        : new Error(error.message || 'Failed to fetch all contacts');
    }
  },

  async fetchVisitors(retryCount = 0): Promise<CustomersResponse> {
    try {
      console.log(`Attempting to fetch customers (try ${retryCount + 1}/${MAX_RETRIES + 1})`);

      // Get workspace ID from localStorage
      const workspaceId = localStorage.getItem('workspaceId');
      console.log('Fetching customers with workspace ID:', workspaceId);


      const response = await HttpClient.apiClient.get<CustomersResponse>(ALL_CONTACTS_URL, {
        params: {
          customer_type: 'visitor'
        }
      });
      console.log(`Successfully fetched customers:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching customers:', error);

      // Check if it's a timeout error
      if (error.isTimeoutError || error.code === 'ECONNABORTED') {
        console.log(`Customer fetch timed out`);
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying fetchVisitors (${retryCount + 1}/${MAX_RETRIES})...`);
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, retryCount)));
          return this.fetchVisitors(retryCount + 1);
        }

        throw new Error('Request timed out after multiple attempts. Please try again later.');
      }

      // Implement retry for network errors or 5xx server errors
      if (retryCount < MAX_RETRIES && (error.isServerError || error.isOfflineError || !navigator.onLine)) {
        console.log(`Retrying fetchVisitors (${retryCount + 1}/${MAX_RETRIES})...`);
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, retryCount)));
        return this.fetchVisitors(retryCount + 1);
      }

      throw error.isServerError
        ? new Error('Server error. Please try again later.')
        : new Error(error.message || 'Failed to fetch customers');
    }
  },

  async fetchCustomers(retryCount = 0): Promise<CustomersResponse> {
    try {
      console.log(`Attempting to fetch customers (try ${retryCount + 1}/${MAX_RETRIES + 1})`);

      // Get workspace ID from localStorage
      const workspaceId = localStorage.getItem('workspaceId');
      console.log('Fetching customers with workspace ID:', workspaceId);


      const response = await HttpClient.apiClient.get<CustomersResponse>(ALL_CONTACTS_URL, {
        params: {
          customer_type: 'customer'
        }
      });
      console.log(`Successfully fetched customers:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching customers:', error);

      // Check if it's a timeout error
      if (error.isTimeoutError || error.code === 'ECONNABORTED') {
        console.log(`Customer fetch timed out`);
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying fetchCustomers (${retryCount + 1}/${MAX_RETRIES})...`);
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, retryCount)));
          return this.fetchCustomers(retryCount + 1);
        }

        throw new Error('Request timed out after multiple attempts. Please try again later.');
      }

      // Implement retry for network errors or 5xx server errors
      if (retryCount < MAX_RETRIES && (error.isServerError || error.isOfflineError || !navigator.onLine)) {
        console.log(`Retrying fetchCustomers (${retryCount + 1}/${MAX_RETRIES})...`);
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, retryCount)));
        return this.fetchCustomers(retryCount + 1);
      }

      throw error.isServerError
        ? new Error('Server error. Please try again later.')
        : new Error(error.message || 'Failed to fetch customers');
    }
  },
};
